import { createBrowserRouter, Navigate } from 'react-router-dom';

import { ROUTE_PERMISSIONS } from '@/shared/lib/constants/routes';
import { RouteErrorFallback } from '@/shared/ui/ErrorBoundary';
import { MainLayout } from '@/shared/ui/layout';
import { ProtectedRoute } from '@/shared/ui/ProtectedRoute';

import { DataSearch } from '@/features/dataSearch/ui/DataSearch';

import { AdminPage } from '@/pages/admin/AdminPage';
import { PerformanceMonitoringPage } from '@/pages/admin/PerformanceMonitoringPage';
import { AssetDetailPage } from '@/pages/asset-detail/AssetDetailPage';
import { CallbackPage } from '@/pages/auth/CallbackPage';
import { DemoLoginPage } from '@/pages/auth/DemoLoginPage';
import { UnauthorizedPage } from '@/pages/auth/UnauthorizedPage';
import { ConnectorsPage } from '@/pages/connectors/ConnectorsPage';
import { DagDetailPage } from '@/pages/dag-detail';
import { DagListPage } from '@/pages/dag/DagListPage';
import { DataQualityDashboardPage } from '@/pages/data-quality/dashboard/DataQualityDashboardPage';
import { DomainPage } from '@/pages/domain/DomainPage';
import { ExperimentAssetDetailPage } from '@/pages/experiment-asset-detail';
import { ExperimentWorkbenchPage } from '@/pages/experiment-workbench';
import ExperimentsPage from '@/pages/experiments/ExperimentsPage';
import { GlossaryPage } from '@/pages/glossary/GlossaryPage';
import { HomePage } from '@/pages/home/HomePage';
import { LineagePage } from '@/pages/lineage/LineagePage';
import { MonitoringOverviewPage } from '@/pages/monitoring/MonitoringOverviewPage';
import { SearchPage } from '@/pages/search/SearchPage';
import { RoleManagementPage } from '@/pages/settings/RoleManagementPage';
import { UserManagementPage } from '@/pages/settings/UserManagementPage';
import { TagDetailRedirectPage } from '@/pages/tags/TagDetailRedirectPage';
import { TagsPage } from '@/pages/tags/TagsPage';

import { LocationHistoryProvider } from '@/app/providers/LocationHistoryProvider';
import { RootOutlet } from '@/app/routes/RootOutlet';

export const router = createBrowserRouter([
  {
    element: <RootOutlet />,
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: 'login',
        element: <DemoLoginPage />,
      },
      {
        path: '/',
        element: (
          <>
            <LocationHistoryProvider />
            <MainLayout searchBar={<DataSearch />} />
          </>
        ),
        children: [
          {
            element: <ProtectedRoute requiredRoles={ROUTE_PERMISSIONS.AUTHENTICATED} />,
            children: [
              {
                index: true,
                element: <HomePage />,
              },
              {
                path: 'search',
                element: <SearchPage />,
              },
              {
                path: 'domain/:domainFqn?/:tab?',
                element: <DomainPage />,
              },
              {
                path: 'dag',
                element: <DagListPage />,
              },
              {
                path: 'dag-detail/:dagId',
                element: <DagDetailPage />,
              },
              {
                path: 'service/:serviceType/:serviceName/:tab?',
                element: <AssetDetailPage />,
              },
              {
                path: ':entityType/:fqn/:tab?',
                element: <AssetDetailPage />,
              },

              {
                path: 'glossary/:termCategory?/:termFqn?/:tab?',
                element: <GlossaryPage />,
              },
              {
                path: 'tags/:tagCategory?/:tagFqn?',
                element: <TagsPage />,
              },
              {
                path: 'tags/detail/:tagFqn',
                element: <TagDetailRedirectPage />,
              },
              {
                path: 'lineage',
                element: <LineagePage />,
              },
              {
                path: 'connectors',
                element: <ConnectorsPage />,
              },
              {
                path: 'experiments/:projectId?/:experimentId?',
                element: <ExperimentsPage />,
              },
              {
                path: 'experiments/workbench/:runId',
                element: <ExperimentWorkbenchPage />,
              },
              {
                path: 'experiments/:projectId/:experimentId/:runId',
                element: <ExperimentAssetDetailPage />,
              },
              {
                path: 'experiments/:projectId/:experimentId/:runId/:assetId',
                element: <ExperimentAssetDetailPage />,
              },
              {
                path: 'quality/:tab?',
                element: <DataQualityDashboardPage />,
              },
            ],
          },
          {
            path: 'admin',
            element: <ProtectedRoute requiredRoles={ROUTE_PERMISSIONS.ADMIN_AND_MANAGER} />,
            children: [
              {
                index: true,
                element: <AdminPage />,
              },
              {
                path: 'users',
                element: <UserManagementPage />,
              },
              {
                path: 'roles',
                element: <RoleManagementPage />,
              },
              {
                path: 'monitoring',
                element: <MonitoringOverviewPage />,
              },
              {
                path: 'performance',
                element: (
                  <ProtectedRoute requiredRoles={ROUTE_PERMISSIONS.ADMIN_ONLY}>
                    <PerformanceMonitoringPage />
                  </ProtectedRoute>
                ),
              },
            ],
          },
        ],
      },
      {
        path: 'callback',
        element: <CallbackPage />,
      },
      {
        path: 'unauthorized',
        element: <UnauthorizedPage />,
      },
      {
        path: '*',
        element: <Navigate to="/" replace />,
      },
    ],
  },
]);
